import { useState } from 'react'
import { motion } from 'framer-motion'
import { Helmet } from 'react-helmet-async'
import PageHeader from '../components/PageHeader'
import DonateModal from '../components/DonateModal'
import { CalendarIcon, MapPinIcon, UserGroupIcon, HeartIcon } from '@heroicons/react/24/outline'

// Import images
import gmcFrontView from '../assets/images/GOODWILL MEDICAL CENTRES/Goodwill-Medical-centre-front-view.jpeg'

const highlights = [
  {
    title: "Commissioned November 2023",
    description: "Officially opened by General TY Danjuma, serving communities since February 2024",
    icon: CalendarIcon,
  },
  {
    title: "Strategic Location",
    description: "Located in Umuchigbo, serving Ifo, Ugbo-Ezeji, and Ugbo-Paul communities",
    icon: MapPinIcon,
  },
  {
    title: "Community Impact",
    description: "Serving 500+ families with essential healthcare services",
    icon: UserGroupIcon,
  },
  {
    title: "Specialized Care",
    description: "Weekly eye clinics, pediatric care, and maternal health services",
    icon: HeartIcon,
  },
]

const initiatives = [
  {
    title: "Avah River Bridge Project",
    description: "A multi-million Naira bridge connecting three urban slums to essential services, dramatically improving healthcare access for residents who previously had to wade through the river or take lengthy alternative routes.",
    date: "Completed April 2025",
  },
  {
    title: "Traditional Birth Attendants Program",
    description: "Training and empowerment of 21 Traditional Birth Attendants (TBAs) in collaboration with Enugu State Primary Healthcare Development Agency, including geo-spatial mapping across five communities.",
    date: "Ongoing Program",
  },
  {
    title: "Eye Health Services",
    description: "Regular eye clinics by ophthalmologists and optometrists every Tuesday and Thursday, with monthly cataract surgeries sponsored initially by TY Danjuma Foundation.",
    date: "Bi-weekly Service",
  },
  {
    title: "Water Project",
    description: "Completed water harvesting and purification project utilizing Avah River water in collaboration with TY Danjuma Foundation.",
    date: "Completed 2024",
  },
]

function GoodwillMedical() {
  const [donateModalOpen, setDonateModalOpen] = useState(false)

  return (
    <>
      <Helmet>
        <title>Goodwill Medical Centre | PACIEH</title>
        <meta
          name="description"
          content="Discover how Goodwill Medical Centre is transforming healthcare access in Enugu communities through innovative initiatives and community-centered care."
        />
      </Helmet>

      <PageHeader
        title="GOODWILL MEDICAL CENTRE"
        subtitle="Revolutionizing healthcare delivery and essential services for underserved communities in Enugu State."
      />

      {/* Hero Section */}
      <section className="relative py-16">
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="text-3xl font-bold text-pacieh-blue mb-6">
                Transforming Lives Through Healthcare Innovation
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                The Goodwill Medical Centre (GMC) stands as a beacon of hope for underserved communities,
                demonstrating how private initiatives can complement government efforts in transforming
                lives through innovative healthcare solutions and community engagement.
              </p>
              <div className="grid grid-cols-2 gap-6">
                {highlights.map((highlight, index) => (
                  <motion.div
                    key={highlight.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="flex flex-col gap-2"
                  >
                    <div className="flex items-center gap-2">
                      <highlight.icon className="h-6 w-6 text-pacieh-green" />
                      <h3 className="font-semibold text-pacieh-blue">{highlight.title}</h3>
                    </div>
                    <p className="text-sm text-gray-600">{highlight.description}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="relative h-[400px] rounded-2xl overflow-hidden shadow-xl"
            >
              <img
                src={gmcFrontView}
                alt="Goodwill Medical Centre Front View"
                className="w-full h-full object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Avah Bridge Impact Section */}
      <section className="py-16 bg-gray-50">
        <div className="container">
          <div className="grid grid-cols-1 gap-12">
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="text-3xl font-bold text-pacieh-blue mb-6">
                The Avah Bridge Project
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                A testament to our commitment to community welfare, the Avah Bridge has transformed
                access to healthcare for residents who previously had to wade through dangerous waters
                or take costly alternative routes to reach medical care.
              </p>
              <blockquote className="border-l-4 border-pacieh-green pl-4 mb-6 italic text-gray-600">
                "Before the bridge, we lost lives during emergencies. Now we arrive at the hospital
                dry and alive." — Community elder, Ugbo-Ezeji
              </blockquote>
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-semibold text-pacieh-blue mb-4">Impact Highlights</h3>
                <ul className="space-y-3 text-gray-600">
                  <li className="flex items-start gap-2">
                    <span className="h-6 w-6 rounded-full bg-pacieh-green/10 flex items-center justify-center">✓</span>
                    <span>Reduced emergency response time from hours to minutes</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="h-6 w-6 rounded-full bg-pacieh-green/10 flex items-center justify-center">✓</span>
                    <span>Increased access to maternal healthcare services</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="h-6 w-6 rounded-full bg-pacieh-green/10 flex items-center justify-center">✓</span>
                    <span>Enhanced community integration and development</span>
                  </li>
                </ul>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Key Initiatives Section */}
      <section className="py-16">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-pacieh-blue mb-4">
              Our Key Initiatives
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Through strategic partnerships and community engagement, we're implementing
              comprehensive healthcare solutions that address critical needs.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {initiatives.map((initiative, index) => (
              <motion.div
                key={initiative.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow"
              >
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-sm font-medium text-pacieh-green">{initiative.date}</span>
                </div>
                <h3 className="text-xl font-semibold text-pacieh-blue mb-2">{initiative.title}</h3>
                <p className="text-gray-600">{initiative.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="py-16 bg-pacieh-blue text-white">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="max-w-2xl mx-auto text-center"
          >
            <h2 className="text-3xl font-bold mb-6">Support Our Mission</h2>
            <p className="text-lg text-gray-300 mb-8">
              Join us in our mission to provide quality healthcare to underserved communities.
              Your support can help us expand our services and reach more people in need.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                type="button"
                onClick={() => setDonateModalOpen(true)}
                className="bg-white text-pacieh-blue px-8 py-3 rounded-full font-bold hover:bg-gray-100 transition-colors duration-300 shadow-md"
              >
                Donate Now
              </button>
              <a
                href="https://forms.gle/bss8TuKU6U9xXCZ89"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-pacieh-green text-white px-8 py-3 rounded-full font-medium hover:bg-pacieh-green/90 transition-colors duration-300"
              >
                Volunteer With Us
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Global Donation Modal */}
      <DonateModal
        isOpen={donateModalOpen}
        onClose={() => setDonateModalOpen(false)}
      />
    </>
  )
}

export default GoodwillMedical 